import { ArrowUpRight as ArrowUpRightIcon } from '@phosphor-icons/react';
import * as React from 'react';
import type { ComponentType, FocusEventHandler, SVGProps } from 'react';

import { ITEM_TITLE, META_PILL } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';

interface StarterCardProps {
  title: string;
  description: string;
  /** One of `illustration` or `icon` — the artwork slot, two shapes for it. */
  illustration?: ComponentType<SVGProps<SVGSVGElement>>;
  /**
   * A pre-composed mark (e.g. a bordered icon tile) in place of the bare
   * `illustration` SVG — for starters whose identity is a brand/platform
   * mark rather than a line-art illustration. `default` layout only.
   */
  icon?: React.ReactNode;
  /**
   * Quiet readiness note beside the title for a not-yet-live choice ("Soon").
   * Same treatment `GradientChoiceCard` uses — full-opacity `text-muted-
   * foreground` at `text-detail`, deliberately NOT `META_PILL`, which is
   * uppercase + tracked + bordered and reads as a loud object next to a title.
   * `default` layout only.
   */
  readinessLabel?: string;
  /**
   * A small `META_PILL` badge beside the corner arrow, distinguishing this
   * starter's TYPE from a sibling card in the same rail (e.g. "Agent" next
   * to an ads-agent card, sitting beside provider-choice cards that have
   * none). Unlike `readinessLabel`, this IS the loud `META_PILL` treatment —
   * a type badge is meant to be noticed at a glance. `default` layout only.
   */
  cornerLabel?: string;
  /**
   * `default` is the Home/catalog anatomy: artwork above copy.
   * `rail` is the low, horizontal Projects anatomy: copy left, artwork right.
   */
  layout?: 'default' | 'rail';
  onClick: () => void;
  onFocus?: FocusEventHandler<HTMLButtonElement>;
  className?: string;
  disabled?: boolean;
}

/**
 * Illustrated starter shared by the Home composer, contextual rails, and
 * catalog grids. The parent owns width and scrolling; `layout` owns anatomy.
 */
export const StarterCard = React.forwardRef<HTMLButtonElement, StarterCardProps>(
  (
    {
      title,
      description,
      illustration: Illustration,
      icon,
      readinessLabel,
      cornerLabel,
      layout = 'default',
      onClick,
      onFocus,
      className,
      disabled,
    },
    ref,
  ) => (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      onFocus={onFocus}
      disabled={disabled}
      className={cn(
        // `bg-card`, not `bg-transparent` — the latter let whatever sits
        // behind the card (Home's decorative background art) show straight
        // through it, which read as broken (founder, 2026-08-19, screenshot:
        // a flower stem visibly crossing through the LinkedIn Ads card).
        // Matches `.choice-card` (GradientChoiceCard, the Campaigns page's
        // provider chooser — the closest sibling to this card's provider
        // variant), which already uses `background-color: hsl(var(--card))`.
        'group relative flex w-full min-w-0 flex-col overflow-hidden rounded-xl border border-border/50 bg-card p-4 text-left transition-colors',
        'hover:border-border hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
        'disabled:pointer-events-none disabled:opacity-50',
        layout === 'rail' && 'min-h-28 justify-center py-3',
        className,
      )}
    >
      {layout === 'default' ? (
        <>
          {cornerLabel ? (
            <span className={cn(META_PILL, 'absolute right-8 top-2.5')}>{cornerLabel}</span>
          ) : null}
          <ArrowUpRightIcon
            aria-hidden="true"
            className="absolute right-3 top-3 size-3.5 text-muted-foreground/30 transition-colors group-hover:text-foreground/60"
          />
          <div className="flex h-14 items-center">
            {icon ??
              (Illustration && (
                <Illustration
                  className="h-13 w-auto motion-safe:transition-transform motion-safe:duration-300 motion-safe:group-hover:scale-[1.04]"
                  aria-hidden
                />
              ))}
          </div>
          <div className="mt-2 w-full min-w-0 space-y-0.5">
            <div
              className="flex min-w-0 flex-wrap items-baseline gap-x-2"
              data-slot="starter-title-row"
            >
              <div className="min-w-0 max-w-full line-clamp-2 break-words text-sm font-medium leading-snug text-foreground/90">
                {title}
              </div>
              {readinessLabel ? (
                <span className="shrink-0 text-detail font-normal text-muted-foreground">
                  {readinessLabel}
                </span>
              ) : null}
            </div>
            <div className="line-clamp-2 text-detail leading-snug text-muted-foreground">
              {description}
            </div>
          </div>
        </>
      ) : (
        <div className="flex w-full min-w-0 items-center gap-3">
          <div className="min-w-0 flex-1 space-y-1">
            <div className={cn('line-clamp-2 leading-snug', ITEM_TITLE)}>{title}</div>
            <div className="line-clamp-2 text-detail leading-snug text-muted-foreground">
              {description}
            </div>
          </div>
          <div className="flex w-12 shrink-0 items-center justify-center">
            {Illustration && (
              <Illustration
                className="h-10 w-12 opacity-50 transition-opacity duration-200 group-hover:opacity-70 dark:opacity-40 dark:group-hover:opacity-60"
                aria-hidden
              />
            )}
          </div>
        </div>
      )}
    </button>
  ),
);

StarterCard.displayName = 'StarterCard';
